/**
 * Low-level fetch wrapper.
 *
 * - All requests go through `/api/*`. In dev the Vite proxy forwards to
 *   `http://127.0.0.1:8091`; in prod Caddy reverse-proxies the same path.
 *   Same-origin everywhere keeps cookies/sessions simple if we ever add auth.
 *
 * - Every response is parsed with a Zod schema. We do NOT trust the wire
 *   shape — if the backend drifts, ApiError surfaces immediately.
 *
 * - Errors throw a typed `ApiError` so React Query can branch on status.
 */

import { ZodError, type ZodTypeAny, type z } from "zod";
import { HTTPValidationError } from "./schemas";

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  public readonly validationDetails?: string[];

  constructor(message: string, status: number, body: unknown, validationDetails?: string[]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    if (validationDetails) this.validationDetails = validationDetails;
  }

  public get isNotFound(): boolean {
    return this.status === 404;
  }

  public get isValidation(): boolean {
    return this.status === 422;
  }
}

type RequestInitWithoutBody = Omit<RequestInit, "body" | "method">;

interface RequestOptions<S extends ZodTypeAny | null> {
  schema: S;
  init?: RequestInitWithoutBody;
}

/**
 * Global 401 handler.
 *
 * Mutations that fire after a user's session expired would otherwise fail
 * silently — the ApiError surfaces in the page but the user has no signal
 * that they need to log in again. This boots them to /login?next=<here>
 * so they can resume after re-auth.
 *
 * Skipped for:
 *   - Anything under /api/auth/* (login/signup return 401 on bad creds and
 *     the page renders that inline; /me's 401 is the signed-out baseline
 *     useSession converts to null).
 *   - Non-browser environments (SSR/tests without a window).
 *   - When already on /login or /signup (avoid redirect loops).
 */
function maybeRedirectOn401(status: number, path: string): void {
  if (status !== 401) return;
  if (path.startsWith("/api/auth/")) return;
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  if (pathname === "/login" || pathname === "/signup") return;
  const next = encodeURIComponent(pathname + search);
  try {
    window.location.assign(`/login?next=${next}`);
  } catch {
    // jsdom and some test envs don't implement navigation; the ApiError
    // still propagates so the test can assert against it.
  }
}

async function parseJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function pickValidationDetails(body: unknown): string[] | undefined {
  const parsed = HTTPValidationError.safeParse(body);
  if (!parsed.success || !parsed.data.detail) return undefined;
  return parsed.data.detail.map((d) => {
    const path = d.loc.filter((p) => p !== "body").join(".");
    return path ? `${path}: ${d.msg}` : d.msg;
  });
}

/**
 * Pull a human-readable message out of a FastAPI error body whose `detail`
 * is something OTHER than the array-shape pydantic validation envelope.
 *
 * Backend handlers raise structured errors like
 *   raise HTTPException(status_code=422, detail={
 *     "code": "missing_inputs",
 *     "hint": "Add a base description before generating.",
 *   })
 * or the plain-string form
 *   raise HTTPException(status_code=404, detail="Character not found")
 *
 * Returns the first usable string (`hint` > `message` > `detail` as string)
 * or undefined if nothing fits. The array-shape pydantic envelope is handled
 * separately by `pickValidationDetails` and short-circuits this path.
 */
export function pickStructuredDetail(body: unknown): string | undefined {
  if (!body || typeof body !== "object" || !("detail" in body)) return undefined;
  const detail = (body as { detail: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const d = detail as { hint?: unknown; message?: unknown };
    if (typeof d.hint === "string" && d.hint.length > 0) return d.hint;
    if (typeof d.message === "string" && d.message.length > 0) return d.message;
  }
  return undefined;
}

async function send<S extends ZodTypeAny | null>(
  method: string,
  path: string,
  body: unknown,
  options: RequestOptions<S>,
): Promise<S extends ZodTypeAny ? z.infer<S> : void> {
  const init: RequestInit = {
    ...options.init,
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(options.init?.headers ?? {}),
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (err) {
    // Network-level failure: DNS, offline, CORS, etc.
    throw new ApiError(
      err instanceof Error ? err.message : "Network error",
      0,
      null,
    );
  }

  if (response.status === 204) {
    return undefined as S extends ZodTypeAny ? z.infer<S> : void;
  }

  const responseBody = await parseJsonSafe(response);

  if (!response.ok) {
    const details = pickValidationDetails(responseBody);
    const structured = pickStructuredDetail(responseBody);
    let msg = `${method} ${path} failed with ${response.status}`;
    if (
      responseBody &&
      typeof responseBody === "object" &&
      "message" in responseBody &&
      typeof (responseBody as { message: unknown }).message === "string"
    ) {
      msg = (responseBody as { message: string }).message;
    } else if (details && details.length > 0) {
      msg = details.join("; ");
    } else if (structured) {
      msg = structured;
    }
    maybeRedirectOn401(response.status, path);
    throw new ApiError(msg, response.status, responseBody, details);
  }

  if (options.schema === null) {
    return undefined as S extends ZodTypeAny ? z.infer<S> : void;
  }
  try {
    return options.schema.parse(responseBody) as S extends ZodTypeAny ? z.infer<S> : void;
  } catch (err) {
    if (err instanceof ZodError) {
      throw new ApiError(
        `Response shape mismatch for ${method} ${path}: ${err.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ")}`,
        response.status,
        responseBody,
      );
    }
    throw err;
  }
}

export const api = {
  get<S extends ZodTypeAny>(path: string, schema: S, init?: RequestInitWithoutBody) {
    return send("GET", path, undefined, { schema, init });
  },
  post<S extends ZodTypeAny>(path: string, body: unknown, schema: S, init?: RequestInitWithoutBody) {
    return send("POST", path, body ?? null, { schema, init });
  },
  postEmpty<S extends ZodTypeAny>(path: string, schema: S, init?: RequestInitWithoutBody) {
    return send("POST", path, undefined, { schema, init });
  },
  put<S extends ZodTypeAny>(path: string, body: unknown, schema: S, init?: RequestInitWithoutBody) {
    return send("PUT", path, body, { schema, init });
  },
  patch<S extends ZodTypeAny>(path: string, body: unknown, schema: S, init?: RequestInitWithoutBody) {
    return send("PATCH", path, body, { schema, init });
  },
  delete(path: string, init?: RequestInitWithoutBody): Promise<void> {
    return send("DELETE", path, undefined, { schema: null, init });
  },
};

/**
 * User-friendly error messages.
 * Network errors and 5xxs get retry suggestions; 4xxs say what was wrong.
 */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return "Can't reach the server. Check your connection.";
    if (err.status === 404) return "Not found.";
    if (err.status >= 500) {
      const hint = pickStructuredDetail(err.body);
      return hint ?? "Server error. Try again in a moment.";
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
